import { NavLink, useNavigate } from 'react-router-dom'
import { useStore } from '../store/useStore'
import { auth } from '../services/supabase'
import toast from 'react-hot-toast'

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: '◈' },
  { path: '/accounts', label: 'Accounts', icon: '⬡' },
  { path: '/budget', label: 'Budget', icon: '◎' },
  { path: '/investments', label: 'Investments', icon: '◑', gate: 'investment_tracking' },
  { path: '/credit', label: 'Credit Score', icon: '★' },
  { path: '/ai-coach', label: 'AI Coach', icon: '✦', gate: 'ai_coach' },
]

const C = {
  bg: '#0A0F0D', surface: '#111916', border: '#1E2E24',
  mint: '#00C896', text: '#E8F5EE', muted: '#6B8F7A',
}

export default function Layout({ children }) {
  const { user, plan, canUseFeature } = useStore()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await auth.signOut()
    toast.success('Signed out')
    navigate('/')
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, position: 'fixed', top: 0, left: 0, bottom: 0,
        background: C.surface, borderRight: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column', zIndex: 100,
      }}>
        {/* Logo */}
        <div style={{ padding: '28px 22px 24px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, background: C.mint,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, boxShadow: '0 0 16px rgba(0,200,150,0.35)',
            }}>🌿</div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: C.text, lineHeight: 1 }}>
                Peppermint<span style={{ color: C.mint }}>Fi</span>
              </div>
              <div style={{ color: C.mint, fontSize: 9, letterSpacing: '0.15em', marginTop: 2 }}>PERSONAL FINANCE</div>
            </div>
          </div>
        </div>

        {/* Plan badge */}
        <div style={{ padding: '12px 22px', borderBottom: `1px solid ${C.border}` }}>
          <span style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: 99,
            fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
            background: plan === 'premium' ? 'rgba(167,139,250,0.15)' : plan === 'pro' ? 'rgba(0,200,150,0.12)' : 'rgba(107,143,122,0.15)',
            color: plan === 'premium' ? '#A78BFA' : plan === 'pro' ? C.mint : C.muted,
            border: `1px solid ${plan === 'premium' ? 'rgba(167,139,250,0.3)' : plan === 'pro' ? 'rgba(0,200,150,0.25)' : C.border}`,
          }}>
            {plan.toUpperCase()}
          </span>
          {plan === 'free' && (
            <NavLink to="/pricing" style={{ display: 'block', color: C.mint, fontSize: 11, marginTop: 8, textDecoration: 'none' }}>
              Upgrade → Unlock all features
            </NavLink>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {NAV_ITEMS.map(item => {
            const locked = item.gate && !canUseFeature(item.gate)
            return (
              <NavLink key={item.path} to={locked ? '/pricing' : item.path}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                  padding: '11px 12px', borderRadius: 10, marginBottom: 4,
                  textDecoration: 'none',
                  background: isActive && !locked ? 'rgba(0,200,150,0.1)' : 'transparent',
                  borderLeft: `2px solid ${isActive && !locked ? C.mint : 'transparent'}`,
                  transition: 'all 0.15s ease',
                })}>
                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, flex: 1 }}>{item.label}</span>
                {locked && <span style={{ fontSize: 10, color: C.muted }}>🔒</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* User footer */}
        <div style={{ padding: '16px 22px', borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,200,150,0.15)',
              border: `1px solid rgba(0,200,150,0.25)`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: C.mint, fontWeight: 700, fontSize: 14,
            }}>
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ color: C.text, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.email || 'User'}
              </div>
            </div>
          </div>
          <button onClick={handleSignOut} style={{
            width: '100%', padding: '8px', borderRadius: 8,
            background: 'transparent', border: `1px solid ${C.border}`,
            color: C.muted, fontSize: 12, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
          }}>Sign out</button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ marginLeft: 220, flex: 1, padding: 28, minHeight: '100vh' }}>
        {children}
      </main>
    </div>
  )
}
